import { useMemo } from 'react';
import { toast } from 'sonner';
import { aiApi, isAiGenerationCancelled } from '@/api/ai';
import { useReportWizard } from '@/pages/create-report/context/ReportWizardContext';
import AIGeneratorCard from './AIGeneratorCard';
import { SECTIONS } from './constants';
import { WizardStepProps } from './types';
import type { SourceActionsMap } from './AIDataSources';

const DEFAULT_REEVALUATION_PERIOD = '6m';

export default function RecommendationsStep({ data, onUpdate }: WizardStepProps) {
    const orientationType: string | null = null;

    const {
        silentSave,
        handleSectionClick,
        sectionRequirements,
        setIsGenerating,
        setGeneratingSectionId,
        beginGeneration,
        endGeneration,
    } = useReportWizard();

    const sourceActions = useMemo<SourceActionsMap>(
        () => ({
            patient_date_of_birth: {
                label: 'Preencher a data de nascimento',
                onClick: () => handleSectionClick(SECTIONS.PATIENT, 'shortcut_card'),
            },
            clinical_history_content: {
                label: 'Escrever a História Clínica',
                onClick: () => handleSectionClick(SECTIONS.CLINICAL_HISTORY, 'shortcut_card'),
            },
            diagnosis_content: {
                label: 'Escrever a Conclusão',
                onClick: () => handleSectionClick(SECTIONS.DIAGNOSIS, 'shortcut_card'),
            },
        }),
        [handleSectionClick],
    );

    const handleAIGenerate = async () => {
        if (!data.id) return;
        setIsGenerating(true);
        setGeneratingSectionId(SECTIONS.RECOMMENDATIONS);
        const generation = beginGeneration();
        await silentSave();
        try {
            // Append new recommendations to existing ones
            const currentContent =
                typeof data.recommendations === 'string'
                    ? data.recommendations
                    : data.recommendations?.content || '';

            // We start the new content block with a break if there was previous content
            const prefix = currentContent ? `${currentContent}<br/><br/>` : '';

            const response = await aiApi.generate(
                data.id,
                'recommendations',
                {
                    focus: orientationType,
                    reevaluation: DEFAULT_REEVALUATION_PERIOD,
                },
                'create',
                undefined,
                generation.options,
            );

            const newContent = prefix + response.content;

            onUpdate((prevData) => ({
                recommendations: {
                    ...(prevData.recommendations || {}),
                    content: newContent,
                },
            }));
            toast.success('Rascunho das recomendações gerado. Revise no laudo ao lado.');
        } catch (error: unknown) {
            // The user stopped it. The section is untouched and no credit was
            // spent, and the button they pressed is answer enough.
            if (isAiGenerationCancelled(error)) return;

            toast.error((error as Error).message || 'Erro ao gerar conteúdo.');
        } finally {
            setIsGenerating(false);
            setGeneratingSectionId(null);
            endGeneration();
        }
    };

    return (
        <div className="flex h-full flex-col space-y-6">
            <AIGeneratorCard
                onGenerate={handleAIGenerate}
                sourceStepId={SECTIONS.RECOMMENDATIONS}
                reportData={data}
                sourceActions={sourceActions}
                backendRequirements={sectionRequirements}
            />
        </div>
    );
}
